import { Main as Container } from 'Components/Main';
import { useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archiveProcess,
  deleteProcessMapping,
  getProcessById,
  importProcessDataMapping,
  unarchiveProcess,
} from 'services/processes-service';
import { getDataCategories } from 'services/mapping-service';
import { DefaultTable } from 'Components/common/DefaultTable';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { Dialog, PageHeader } from 'Components/ui';
import { tableHeader } from './table-header';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import {
  ShowTabBar, ShowTabBtn, ShowGrid,
  ShowMainCard, ShowCardTitle, ShowFieldGrid, ShowFieldFull, ShowFieldLabel, ShowFieldValue,
  ShowSidebarStack, ShowSidebarCard, ShowSidebarTitle,
  ShowSidebarFieldLabel, ShowSidebarFieldValue,
  RelatedDataItem, RelatedDataLabel, RelatedDataCount,
  VulnerableAlert,
  ShowChipWrap, ShowChipTag,
  UserDisplay, UserAvatar,
  CritBadge, BoolBadge,
  DataMapCard, DataMapHeader, DataMapHeaderText, DataMapHeaderTitle, DataMapHeaderSub, DataMapAddBtn,
  BondsGrid, BondCard, BondCardHeader, BondCardTitle, BondChipWrap, BondChip, BondAddBtn,
  ShowHeaderBtn, ShowHeaderBtnSecondary,
  ActionBtns, ActionBtn, ImportCsvBtn,
} from '../styles';
import { formatDate as formatDateIntl, LOCALE_MAP } from 'utils/locale-formatting';

const CRIT_STYLES = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};

const WarnSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--vo-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const PlusSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const PlusSmSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const EditSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const ArchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/>
    <rect x="1" y="3" width="22" height="5"/>
    <line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);

const UploadSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

/* Exact same icons as the process list page */
const RowEditSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const RowDeleteSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const RowShowSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const getInitials = name => {
  if (!name) return 'U';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const ProcessesShow = () => {
  const { t, i18n } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();
  const { id } = useParams();
  const [tabPosition, setTabPosition] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const locale = LOCALE_MAP[i18n.language] ?? 'pt-BR';
  const formatDate = (raw) => formatDateIntl(raw, locale) || '—';
  const [open, setOpen] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  const [itemDeleteId, setItemDeleteId] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const fileInputRef = useRef(null);

  const { isLoading, error, data: process } = useQuery(['getProcessById', refresh], async () => {
    const res = await getProcessById(id);
    return res.status === 200 ? res.data : null;
  });

  const { data: dataCategoriesList } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getDataCategories(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const categoryByKey = new Map((dataCategoriesList ?? []).map(c => [c.key, c.name]));

  const onDelete = (item, itemId) => { setItemDelete(item); setItemDeleteId(itemId); setOpen(true); };
  const onClose  = () => { setOpen(false); setItemDelete(null); setItemDeleteId(null); };

  const onConfirm = async () => {
    try {
      const result = await deleteProcessMapping(id, itemDeleteId);
      if (result.status === 200) { onClose(); setRefresh(r => !r); }
      else throw new Error(t('processes.show.toasts.errorDeleteData'));
    } catch (err) {
      Dialog.alert({ title: t('processes.toasts.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const importDataMapping = useMutation(
    content => importProcessDataMapping(id, { file: content }),
    {
      onError: err => {
        setLoadingUpload(false);
        Dialog.alert({ title: t('processes.toasts.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
      },
      onSuccess: data => {
        setLoadingUpload(false);
        if (data.status === 200) {
          Dialog.fire({
            title: t('processes.toasts.successTitle'),
            text: t('processes.show.toasts.importSuccess'),
            icon: 'success',
            didClose: () => setRefresh(r => !r),
          });
        }
      },
    },
  );

  const handleFileChange = e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      Dialog.alert({ title: 'Aviso!', message: 'Por favor, selecione um arquivo CSV.', icon: 'error' });
      return;
    }
    setLoadingUpload(true);
    const reader = new FileReader();
    reader.onload = () => importDataMapping.mutate(reader.result);
    reader.readAsDataURL(file);
  };

  const archiveMutation = useMutation(
    () => (process?.archived ? unarchiveProcess(id) : archiveProcess(id)),
    {
      onSuccess: () => {
        Dialog.fire({
          title: t('processes.toasts.successTitle'),
          text: process?.archived ? t('processes.toasts.unarchived') : t('processes.toasts.archived'),
          icon: 'success',
          didClose: () => setRefresh(r => !r),
        });
      },
      onError: err => {
        Dialog.alert({
          title: t('processes.toasts.errorTitle'),
          message: err.response?.data?.message ?? (process?.archived ? t('processes.toasts.errorUnarchive') : t('processes.toasts.errorArchive')),
          icon: 'error',
        });
      },
    },
  );

  const handleArchive = async () => {
    const actionLabel = process?.archived ? t('processes.show.actions.unarchive') : t('processes.show.actions.archive');
    const confirmed = await Dialog.confirm({
      title: actionLabel,
      message: process?.name,
      confirmLabel: actionLabel,
    });
    if (confirmed) archiveMutation.mutate();
  };

  /* Build table rows with custom stroke-style action icons, bypassing DefaultTable's built-in action renderers */
  const buildDataMappingRows = () => [
    ...tableHeader(t).filter(r => r.id !== 'action'),
    {
      id: 'action',
      type: 'text',
      value: t('processes.table.actions'),
      render: (_val, item, index) => (
        <ActionBtns>
          <ActionBtn
            type="button"
            onClick={() => navigate(`/processes/show/${id}/mapping/${index}`)}
          >
            <RowShowSvg />
          </ActionBtn>
          <ActionBtn
            type="button"
            onClick={() => navigate(`/processes/edit/${id}/mapping/${index}`)}
          >
            <RowEditSvg />
          </ActionBtn>
          <ActionBtn
            type="button"
            $danger
            onClick={() => onDelete(item, index)}
          >
            <RowDeleteSvg />
          </ActionBtn>
        </ActionBtns>
      ),
    },
  ];

  if (isLoading) return <PageLoaderShow />;
  if (error) return <div>{t('processes.show.errorLoad')}</div>;

  const critStyle = CRIT_STYLES[process?.criticity] ?? { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
  const critText  = {
    '1': t('processes.criticality.short.1', 'Muito Baixa'),
    '2': t('processes.criticality.short.2', 'Baixa'),
    '3': t('processes.criticality.short.3', 'Média'),
    '4': t('processes.criticality.short.4', 'Alta'),
    '5': t('processes.criticality.short.5', 'Muito Alta'),
  }[process?.criticity];
  const critLabel = critText && process?.criticity ? `${critText} (${process.criticity})` : '—';

  const activeAreas   = (process?.areas   ?? []).filter(a => !a.archived);
  const activeSystems = (process?.systems ?? []).filter(s => !s.archived);
  const activeAssets  = (process?.assets  ?? []).filter(a => !a.archived);
  const dataCount     = (process?.dataMapping ?? []).length;

  const tabs = [
    t('processes.show.tabs.details'),
    t('processes.show.tabs.datas'),
    t('processes.show.tabs.bonds'),
  ];

  const dataMappingRows = buildDataMappingRows();

  return (
    <Container>
      <PageHeader
        title={process?.name}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('processes.list.title'), route: '/processes' },
          { label: process?.name },
        ]}
        actions={
          <>
            <ShowHeaderBtnSecondary type="button" onClick={handleArchive}>
              <ArchiveSvg />
              {process?.archived ? t('processes.show.actions.unarchive') : t('processes.show.actions.archive')}
            </ShowHeaderBtnSecondary>
            <ShowHeaderBtn type="button" onClick={() => navigate(`/processes/edit/${id}`)}>
              <EditSvg />
              {t('processes.show.actions.edit')}
            </ShowHeaderBtn>
          </>
        }
      />

      <ShowTabBar>
        {tabs.map((label, i) => (
          <ShowTabBtn key={i} $active={tabPosition === i} onClick={() => setTabPosition(i)}>
            {label}
          </ShowTabBtn>
        ))}
      </ShowTabBar>

      {tabPosition === 0 && (
        <ShowGrid>
          <ShowMainCard>
            <ShowCardTitle>{t('processes.show.fields.generalInfo')}</ShowCardTitle>
            <ShowFieldGrid>
              <div>
                <ShowFieldLabel>{t('processes.show.fields.criticality')}</ShowFieldLabel>
                {process?.criticity
                  ? <CritBadge $color={critStyle.color} $bg={critStyle.bg}>{critLabel}</CritBadge>
                  : <ShowFieldValue>—</ShowFieldValue>}
              </div>

              <div>
                <ShowFieldLabel>{t('processes.show.fields.responsible')}</ShowFieldLabel>
                {process?.user ? (
                  <UserDisplay>
                    <UserAvatar>{getInitials(process.user)}</UserAvatar>
                    <ShowFieldValue style={{ margin: 0 }}>{process.user}</ShowFieldValue>
                  </UserDisplay>
                ) : <ShowFieldValue>—</ShowFieldValue>}
              </div>

              <div>
                <ShowFieldLabel>{t('processes.show.fields.dpia')}</ShowFieldLabel>
                <BoolBadge $yes={!!process?.dpia}>
                  {process?.dpia ? t('processes.list.filters.options.yes') : t('processes.list.filters.options.no')}
                </BoolBadge>
              </div>

              <div>
                <ShowFieldLabel>{t('processes.show.fields.lia')}</ShowFieldLabel>
                <BoolBadge $yes={!!process?.lia}>
                  {process?.lia ? t('processes.list.filters.options.yes') : t('processes.list.filters.options.no')}
                </BoolBadge>
              </div>

              <ShowFieldFull>
                <ShowFieldLabel>{t('processes.show.fields.tags')}</ShowFieldLabel>
                {process?.tags?.length > 0 ? (
                  <ShowChipWrap>
                    {process.tags.map(tag => <ShowChipTag key={tag}>{tag}</ShowChipTag>)}
                  </ShowChipWrap>
                ) : (
                  <ShowFieldValue>—</ShowFieldValue>
                )}
              </ShowFieldFull>

              {process?.description && (
                <ShowFieldFull>
                  <ShowFieldLabel>{t('processes.show.fields.description')}</ShowFieldLabel>
                  <ShowFieldValue>{process.description}</ShowFieldValue>
                </ShowFieldFull>
              )}

              {process?.descriptionDataFlow && (
                <ShowFieldFull>
                  <ShowFieldLabel>{t('processes.show.fields.descriptionDataFlow')}</ShowFieldLabel>
                  <ShowFieldValue>{process.descriptionDataFlow}</ShowFieldValue>
                </ShowFieldFull>
              )}

              {process?.dataTreatmentFromChildrenTeenagerElderly && (
                <ShowFieldFull>
                  <VulnerableAlert>
                    <WarnSvg />
                    <div>
                      <p style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 13, fontWeight: 600, color: 'var(--vo-ink-2)', margin: '0 0 3px' }}>
                        {t('processes.show.fields.vulnerableTitle')}
                      </p>
                      <p style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: 'var(--vo-slate)', margin: 0 }}>
                        {t('processes.show.fields.vulnerableDesc')}
                      </p>
                    </div>
                  </VulnerableAlert>
                </ShowFieldFull>
              )}
            </ShowFieldGrid>
          </ShowMainCard>

          <ShowSidebarStack>
            <ShowSidebarCard>
              <ShowSidebarTitle>{t('processes.show.fields.history')}</ShowSidebarTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <ShowSidebarFieldLabel>{t('processes.show.fields.createdAt')}</ShowSidebarFieldLabel>
                  <ShowSidebarFieldValue>{formatDate(process?.createdAt)}</ShowSidebarFieldValue>
                </div>
                <div>
                  <ShowSidebarFieldLabel>{t('processes.show.fields.updatedAt')}</ShowSidebarFieldLabel>
                  <ShowSidebarFieldValue>{formatDate(process?.updatedAt)}</ShowSidebarFieldValue>
                </div>
              </div>
            </ShowSidebarCard>

            <ShowSidebarCard>
              <ShowSidebarTitle>{t('processes.show.relatedData')}</ShowSidebarTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <RelatedDataItem>
                  <RelatedDataLabel>{t('processes.show.relatedAreas')}</RelatedDataLabel>
                  <RelatedDataCount>{activeAreas.length}</RelatedDataCount>
                </RelatedDataItem>
                <RelatedDataItem>
                  <RelatedDataLabel>{t('processes.show.relatedSystems')}</RelatedDataLabel>
                  <RelatedDataCount>{activeSystems.length}</RelatedDataCount>
                </RelatedDataItem>
                <RelatedDataItem>
                  <RelatedDataLabel>{t('processes.show.relatedAssets')}</RelatedDataLabel>
                  <RelatedDataCount>{activeAssets.length}</RelatedDataCount>
                </RelatedDataItem>
                <RelatedDataItem>
                  <RelatedDataLabel>{t('processes.show.relatedPersonalData')}</RelatedDataLabel>
                  <RelatedDataCount>{dataCount}</RelatedDataCount>
                </RelatedDataItem>
              </div>
            </ShowSidebarCard>
          </ShowSidebarStack>
        </ShowGrid>
      )}

      {tabPosition === 1 && (
        <DataMapCard>
          <DataMapHeader>
            <DataMapHeaderText>
              <DataMapHeaderTitle>{t('processes.show.dataMapping.title')}</DataMapHeaderTitle>
              <DataMapHeaderSub>{t('processes.show.dataMapping.subtitle')}</DataMapHeaderSub>
            </DataMapHeaderText>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <ImportCsvBtn
                type="button"
                disabled={loadingUpload}
                onClick={() => !loadingUpload && fileInputRef.current?.click()}
              >
                <UploadSvg />
                {loadingUpload
                  ? t('processes.show.actions.importing')
                  : t('processes.show.actions.import')}
              </ImportCsvBtn>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <DataMapAddBtn
                type="button"
                onClick={() => navigate(`/processes/edit/${id}/mapping`)}
              >
                <PlusSvg />
                {t('processes.show.actions.addMapping')}
              </DataMapAddBtn>
            </div>
          </DataMapHeader>
          <div style={{ padding: '0 4px 4px' }}>
            <DefaultTable
              rows={dataMappingRows}
              id={id}
              hideShow
              hideEdit
              hideDelete
              cells={process.dataMapping?.map(m => ({
                ...m,
                category:
                  m.category === 'other'
                    ? m.customDataCategory ?? m.category
                    : categoryByKey.get(m.category) ?? m.category,
              }))}
              link="processes"
            />
          </div>
          <Dialog
            open={open}
            onConfirm={onConfirm}
            onCancel={onClose}
            message={t('processes.show.modals.deleteDataMessage', { name: itemDelete?.name ?? '' })}
          />
        </DataMapCard>
      )}

      {tabPosition === 2 && (
        <BondsGrid>
          <BondCard>
            <BondCardHeader>
              <BondCardTitle>{t('processes.show.relatedAreas')}</BondCardTitle>
              <BondAddBtn type="button" onClick={() => navigate(`/processes/edit/${id}`)}>
                <PlusSmSvg />
              </BondAddBtn>
            </BondCardHeader>
            <BondChipWrap>
              {activeAreas.length > 0
                ? activeAreas.map(a => <BondChip key={a.name}>{a.name}</BondChip>)
                : <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-slate)' }}>—</span>}
            </BondChipWrap>
          </BondCard>

          <BondCard>
            <BondCardHeader>
              <BondCardTitle>{t('processes.show.fields.systems')}</BondCardTitle>
              <BondAddBtn type="button" onClick={() => navigate(`/processes/edit/${id}`)}>
                <PlusSmSvg />
              </BondAddBtn>
            </BondCardHeader>
            <BondChipWrap>
              {activeSystems.length > 0
                ? activeSystems.map(s => <BondChip key={s.name}>{s.name}</BondChip>)
                : <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-slate)' }}>—</span>}
            </BondChipWrap>
          </BondCard>

          <BondCard>
            <BondCardHeader>
              <BondCardTitle>{t('processes.show.fields.assets')}</BondCardTitle>
              <BondAddBtn type="button" onClick={() => navigate(`/processes/edit/${id}`)}>
                <PlusSmSvg />
              </BondAddBtn>
            </BondCardHeader>
            <BondChipWrap>
              {activeAssets.length > 0
                ? activeAssets.map(a => <BondChip key={a.name}>{a.name}</BondChip>)
                : <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-slate)' }}>—</span>}
            </BondChipWrap>
          </BondCard>
        </BondsGrid>
      )}
    </Container>
  );
};
